/**
 * Small thumbnail for a model.
 *
 * Renders the avatar at `$icon` when present; otherwise shows the discover
 * page's tinted globe placeholder. If the image fails to load (404, etc.)
 * we fall back to the placeholder as well.
 */
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import ApiImage from './ApiImage';

interface Props {
  /** Value of the model's `$icon` field — relative API path like `api/models/{id}/avatar`. */
  src?: string;
  /** Outer square size in pixels. Default 36. */
  size?: number;
  /** Fallback FA icon. Default globe. */
  fallback?: IconDefinition;
  /** Border radius. Default 12px (matches existing card placeholders). */
  radius?: number;
  className?: string;
}

export default function ModelIcon({ src, size = 36, fallback = faGlobe, radius = 12, className }: Readonly<Props>) {
  const [err, setErr] = useState(false);
  const showImg = !!src && !err;

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: 'rgb(var(--brew-blue-light) / 0.06)',
        border: '1px solid rgb(var(--brew-blue-light) / 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
      aria-hidden
    >
      {showImg ? (
        <ApiImage
          src={src}
          alt=""
          loading="lazy"
          onError={() => setErr(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <FontAwesomeIcon
          icon={fallback}
          style={{ color: 'rgb(var(--brew-blue-light) / 0.5)', fontSize: Math.round(size * 0.44) }}
        />
      )}
    </div>
  );
}
